import React, { useState } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import {
  Settings as SettingsIcon, Lock, Bell, Eye,
  Smartphone, ShieldCheck, Zap, LogOut,
  CheckCircle, AlertCircle, Loader2, Eye as EyeIcon, EyeOff
} from "lucide-react";
import api from "../services/api";
import { useAuth } from "../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const TABS = {
  security: "security",
};

export default function Settings() {
  const { t } = useTranslation();
  const { logout, user } = useAuth();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState(TABS.security);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);

  // Password form state
  const [pwForm, setPwForm] = useState({ current: "", newPw: "", confirm: "" });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Settings state (NOT USED but kept for API compatibility if needed)
  const [userSettings, setUserSettings] = useState({});

  React.useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await api.get('/users/settings');
        if (res.success && res.data) {
          setUserSettings(res.data);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    fetchSettings();
  }, []);

  // 2FA state
  const [twoFactorData, setTwoFactorData] = useState({
    enabled: !!user?.two_factor_enabled,
    qrCode: null,
    secret: ""
  });
  const [show2FAModal, setShow2FAModal] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState("");

  React.useEffect(() => {
    if (user && user.two_factor_enabled !== undefined) {
      setTwoFactorData(prev => ({ ...prev, enabled: !!user.two_factor_enabled }));
    }
  }, [user]);


  const menuItems = [
    { id: TABS.security, icon: <Lock size={20} />, label: t('settings.security_password'), desc: t('settings.security_desc') },
  ];

  const showFeedback = (type, msg) => {
    if (type === "success") { setSuccessMsg(msg); setErrorMsg(""); }
    else { setErrorMsg(msg); setSuccessMsg(""); }
    setTimeout(() => { setSuccessMsg(""); setErrorMsg(""); }, 4000);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwForm.current || !pwForm.newPw || !pwForm.confirm) {
      return showFeedback("error", t('auth.fill_all_fields'));
    }
    if (pwForm.newPw.length < 8) {
      return showFeedback("error", "Password must be at least 8 characters.");
    }
    if (pwForm.newPw !== pwForm.confirm) {
      return showFeedback("error", t('auth.passwords_dont_match'));
    }
    try {
      setLoading(true);
      await api.put("/users/change-password", {
        current_password: pwForm.current,
        new_password: pwForm.newPw,
      });
      setPwForm({ current: "", newPw: "", confirm: "" });
      showFeedback("success", t('settings.update_password') + " " + t('profile.profile_updated'));
    } catch (err) {
      showFeedback("error", err.message || t('auth.tech_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setLoading(true);
      await api.put("/users/settings", userSettings);
      showFeedback("success", t('settings.notif_saved'));
    } catch (err) {
      showFeedback("error", t('profile.update_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSetup2FA = async () => {
    try {
      setLoading(true);
      const res = await api.post("/auth/2fa/setup");
      setTwoFactorData(prev => ({ ...prev, qrCode: res.qrCode, secret: res.secret }));
      setShow2FAModal(true);
    } catch (err) {
      showFeedback("error", "Erreur lors de la configuration 2FA");
    } finally {
      setLoading(false);
    }
  };

  const handleActivate2FA = async () => {
    try {
      setLoading(true);
      await api.post("/auth/2fa/activate", { token: twoFactorCode });
      setTwoFactorData(prev => ({ ...prev, enabled: true }));
      setShow2FAModal(false);
      showFeedback("success", "Authentification 2FA activée !");
    } catch (err) {
      showFeedback("error", "Code invalide. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };


  const handleLogoutAll = () => {
    logout();
    navigate("/login");
  };

  const getStrengthLabel = (val) => {
    if (val.length >= 12) return t('settings.password_strength.strong');
    if (val.length >= 10) return t('settings.password_strength.good');
    if (val.length >= 8) return t('settings.password_strength.medium');
    return t('settings.password_strength.weak');
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white font-inter">
      <Header />

      <div className="fixed top-0 left-1/4 w-full max-w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[150px] pointer-events-none" />

      <main className="relative max-w-7xl mx-auto px-6 lg:px-8 py-32">

        {/* Feedback */}
        {successMsg && (
          <div className="mb-6 px-6 py-4 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl text-emerald-400 font-bold text-sm flex items-center gap-3">
            <CheckCircle size={18} /> {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-6 px-6 py-4 bg-rose-500/10 border border-rose-500/30 rounded-2xl text-rose-400 font-bold text-sm flex items-center gap-3">
            <AlertCircle size={18} /> {errorMsg}
          </div>
        )}

        <div className="flex flex-col lg:flex-row gap-16">

          {/* Sidebar Menu */}
          <div className="lg:w-1/3 space-y-8">
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/5 rounded-lg mb-4">
                <SettingsIcon size={14} className="text-indigo-400" />
                <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest leading-none">{t('settings.sys_config')}</span>
              </div>
              <h1 className="text-4xl font-black font-outfit uppercase tracking-tight">
                {t('settings.advanced_settings').split(' ').slice(0, -1).join(' ')} <span className="text-indigo-400">{t('settings.advanced_settings').split(' ').slice(-1)}</span>
              </h1>
              <p className="text-slate-400 font-medium">{t('settings.settings_desc')}</p>
            </div>

            <nav className="space-y-2">
              {menuItems.map((item, idx) => (
                <button
                  key={idx}
                  onClick={() => item.id && setActiveTab(item.id)}
                  className={`w-full glass-card p-6 text-left transition-all group flex items-start gap-6 ${activeTab === item.id
                      ? "border-indigo-500/40 bg-indigo-500/5"
                      : "border-white/5 hover:border-indigo-500/20"
                    }`}
                >
                  <div className={`mt-1 p-3 rounded-xl border border-white/5 transition-all group-hover:scale-110 ${activeTab === item.id ? "bg-indigo-500/20 text-indigo-400" : "bg-white/5 text-slate-500 group-hover:text-indigo-400"
                    }`}>
                    {item.icon}
                  </div>
                  <div>
                    <p className={`text-sm font-black uppercase tracking-tight mb-1 ${activeTab === item.id ? "text-indigo-400" : "text-white"}`}>
                      {item.label}
                    </p>
                    <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest leading-relaxed">{item.desc}</p>
                  </div>
                </button>
              ))}
            </nav>

            <button
              onClick={handleLogoutAll}
              className="w-full p-6 bg-rose-500/5 hover:bg-rose-500/10 border border-rose-500/20 rounded-2xl flex items-center justify-center gap-3 text-rose-400 transition-all font-black text-[10px] uppercase tracking-widest"
            >
              <LogOut size={16} />
              {t('settings.logout_all')}
            </button>
          </div>

          {/* Content Area */}
          <div className="lg:w-2/3">

            {/* ─── TAB: SECURITY ─── */}
            {activeTab === TABS.security && (
              <div className="glass-card p-10 border-white/5 shadow-2xl relative overflow-hidden bg-gradient-to-br from-slate-900 to-transparent">
                <div className="flex items-center justify-between mb-12">
                  <div className="space-y-1">
                    <h3 className="text-2xl font-black font-outfit uppercase tracking-tight">{t('settings.account_security').split(' ').slice(0, -1).join(' ')} <span className="text-indigo-400">{t('settings.account_security').split(' ').slice(-1)}</span></h3>
                    <p className="text-xs font-bold text-slate-500">{t('settings.account_security_desc')}</p>
                  </div>
                </div>

                <div className="space-y-12">
                  {/* 2FA */}
                  <div className="flex items-center justify-between p-6 bg-white/[0.01] border border-white/5 rounded-2xl">
                    <div className="flex items-center gap-6">
                      <div className="p-4 bg-emerald-500/10 rounded-2xl text-emerald-500 border border-emerald-500/20">
                        <Smartphone size={32} />
                      </div>
                      <div>
                        <h4 className="text-lg font-black font-outfit uppercase tracking-tight">{t('settings.auth_2fa')}</h4>
                        <p className="text-xs font-bold text-slate-500">{t('settings.auth_2fa_desc')}</p>
                      </div>
                    </div>
                    {twoFactorData.enabled ? (
                      <div className="flex items-center gap-2 px-3 py-1 bg-emerald-500/10 text-emerald-400 rounded-full border border-emerald-500/20">
                        <CheckCircle size={12} />
                        <span className="text-[10px] font-black uppercase tracking-widest">Activé</span>
                      </div>
                    ) : (
                      <button
                        onClick={handleSetup2FA}
                        disabled={loading}
                        className="px-6 py-2 bg-indigo-500 hover:bg-indigo-600 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg shadow-indigo-500/20"
                      >
                        {loading ? <Loader2 size={14} className="animate-spin" /> : "Activer"}
                      </button>
                    )}
                  </div>


                  <div className="h-px bg-white/5 w-full" />

                  {/* Change Password */}
                  <div>
                    <h4 className="text-xl font-black font-outfit uppercase tracking-tight mb-6">{t('settings.change_password')}</h4>
                    <form onSubmit={handleChangePassword} className="space-y-6">
                      {/* Current Password */}
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">{t('settings.current_password')}</label>
                        <div className="relative">
                          <input
                            type={showCurrent ? "text" : "password"}
                            value={pwForm.current}
                            onChange={e => setPwForm(f => ({ ...f, current: e.target.value }))}
                            className="w-full bg-slate-900 border border-white/5 rounded-2xl py-4 px-6 pr-12 text-sm font-bold text-white focus:border-indigo-500/50 outline-none transition-all"
                            placeholder="••••••••••••"
                          />
                          <button type="button" onClick={() => setShowCurrent(!showCurrent)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                            {showCurrent ? <EyeOff size={18} /> : <EyeIcon size={18} />}
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* New Password */}
                        <div className="space-y-3">
                          <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">{t('settings.new_password')}</label>
                          <div className="relative">
                            <input
                              type={showNew ? "text" : "password"}
                              value={pwForm.newPw}
                              onChange={e => setPwForm(f => ({ ...f, newPw: e.target.value }))}
                              className="w-full bg-slate-900 border border-white/5 rounded-2xl py-4 px-6 pr-12 text-sm font-bold text-white focus:border-indigo-500/50 outline-none transition-all"
                              placeholder="••••••••••••"
                            />
                            <button type="button" onClick={() => setShowNew(!showNew)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white transition-colors">
                              {showNew ? <EyeOff size={18} /> : <EyeIcon size={18} />}
                            </button>
                          </div>
                        </div>

                        {/* Confirm */}
                        <div className="space-y-3">
                          <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em] ml-1">{t('settings.confirm_password')}</label>
                          <input
                            type="password"
                            value={pwForm.confirm}
                            onChange={e => setPwForm(f => ({ ...f, confirm: e.target.value }))}
                            className={`w-full bg-slate-900 border rounded-2xl py-4 px-6 text-sm font-bold text-white focus:outline-none transition-all ${pwForm.confirm && pwForm.confirm !== pwForm.newPw
                                ? "border-rose-500/50"
                                : "border-white/5 focus:border-indigo-500/50"
                              }`}
                            placeholder="••••••••••••"
                          />
                        </div>
                      </div>

                      {/* Strength hint */}
                      {pwForm.newPw && (
                        <div className="flex gap-2">
                          {[...Array(4)].map((_, i) => (
                            <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i < (pwForm.newPw.length >= 12 ? 4 : pwForm.newPw.length >= 10 ? 3 : pwForm.newPw.length >= 8 ? 2 : 1)
                                ? ["bg-rose-500", "bg-amber-500", "bg-indigo-500", "bg-emerald-500"][Math.min(i, 3)]
                                : "bg-white/10"
                              }`} />
                          ))}
                          <span className="text-[10px] text-slate-500 font-bold ml-2 whitespace-nowrap">
                            {getStrengthLabel(pwForm.newPw)}
                          </span>
                        </div>
                      )}

                      <button
                        type="submit"
                        disabled={loading}
                        className="btn-premium !py-4 px-10 text-[10px] font-black uppercase tracking-widest shadow-indigo-500/20 flex items-center gap-3"
                      >
                        {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                        {t('settings.update_password')}
                      </button>
                    </form>
                  </div>

                  <div className="h-px bg-white/5 w-full" />

                  {/* Active Sessions */}
                  <div className="space-y-6">
                    <h4 className="text-xl font-black font-outfit uppercase tracking-tight mb-4">{t('settings.active_sessions')}</h4>
                    <div className="space-y-3">
                      <div className="p-6 bg-white/[0.01] border border-white/5 rounded-2xl flex items-center justify-between group">
                        <div className="flex items-center gap-4">
                          <div className="p-2 rounded-lg bg-indigo-500 text-white">
                            <Zap size={16} />
                          </div>
                          <div>
                            <p className="text-sm font-bold text-white tracking-tight">{t('settings.current_session')}</p>
                            <p className="text-[10px] font-black text-slate-600 uppercase tracking-widest">{t('settings.current_device')} • {t('settings.in_progress')}</p>
                          </div>
                        </div>
                        <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{t('settings.in_progress')}</span>
                      </div>
                    </div>
                  </div>

                  {/* Protection Box */}
                  <div className="p-8 glass-card border-amber-500/10 bg-amber-500/5 flex items-start gap-6">
                    <div className="p-3 bg-amber-500/10 rounded-2xl text-amber-500 border border-amber-500/20">
                      <ShieldCheck size={24} />
                    </div>
                    <div className="space-y-2">
                      <h5 className="text-sm font-black font-outfit uppercase tracking-tight text-white mb-1">{t('settings.inst_protection')}</h5>
                      <p className="text-[11px] font-bold text-slate-500 leading-relaxed uppercase tracking-tighter">
                        {t('settings.inst_protection_desc')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Removed Tabs: Notifications and Privacy */}
          </div>
        </div>

      </main>
      <Footer />

      {/* 2FA SETUP MODAL */}
      {show2FAModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm">
          <div className="glass-card w-full max-w-md p-8 border-indigo-500/30 animate-in zoom-in-95 duration-200">
            <div className="text-center space-y-4 mb-8">
              <div className="mx-auto w-16 h-16 bg-indigo-500/10 rounded-2xl flex items-center justify-center text-indigo-400 border border-indigo-500/20">
                <ShieldCheck size={32} />
              </div>
              <h3 className="text-2xl font-black font-outfit uppercase tracking-tight">Configurer le <span className="text-indigo-400">2FA</span></h3>
              <p className="text-xs font-bold text-slate-400 leading-relaxed uppercase">
                Scannez le QR Code avec votre application d'authentification (Google Authenticator, Authy, etc.)
              </p>
            </div>

            <div className="bg-white p-4 rounded-2xl mx-auto w-fit mb-8 shadow-2xl ring-4 ring-indigo-500/10">
              <img src={twoFactorData.qrCode} alt="QR Code 2FA" className="w-48 h-48" />
            </div>

            <div className="space-y-6">
              <div className="space-y-3 text-center">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-[0.2em]">Code de vérification</label>
                <input
                  type="text"
                  maxLength="6"
                  value={twoFactorCode}
                  onChange={e => setTwoFactorCode(e.target.value)}
                  className="w-full bg-slate-900 border border-white/10 rounded-2xl py-4 text-center text-2xl font-black tracking-[0.5em] text-indigo-400 focus:border-indigo-500 outline-none transition-all"
                  placeholder="000000"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setShow2FAModal(false)}
                  className="flex-1 py-4 bg-white/5 hover:bg-white/10 border border-white/5 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all"
                >
                  Annuler
                </button>
                <button
                  onClick={handleActivate2FA}
                  disabled={loading || twoFactorCode.length < 6}
                  className="flex-[2] btn-premium !py-4 text-[10px] font-black uppercase tracking-widest shadow-indigo-500/20"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : "Confirmer l'activation"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>

  );
}
